import { css } from '@emotion/react';
import { noop } from 'lodash';
import { type MemoExoticComponent, memo, type ReactNode } from 'react';

import { RadioGroupContextProvider } from '../radio/contexts/RadioGroup.context';
import { VARIANTS } from './RadioGroup.constants';
import { Radio } from './radio/Radio';

export type RadioGroupProps = {
  /** Selected value. */
  value?: string;
  /** On change function. */
  onChange?: (newValue: string) => void;
  /** Content. */
  children?: ReactNode;
  /** Way the group of radios is displayed. */
  variant?: VARIANTS;
};

export const RadioGroupBase = ({
  value = null,
  onChange = noop,
  children = null,
  variant = VARIANTS.HORIZONTAL,
  ...props
}: RadioGroupProps) => {
  const isVertical = variant === VARIANTS.HORIZONTAL;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: ${isVertical ? 'row' : 'column'};
        align-items: baseline;
        flex-wrap: wrap;
        gap: 10px;
      `}
    >
      <RadioGroupContextProvider {...props} value={value} onChange={onChange}>
        {children}
      </RadioGroupContextProvider>
    </div>
  );
};

RadioGroupBase.displayName = 'RadioGroup';

export const RadioGroup: MemoExoticComponent<typeof RadioGroupBase> & {
  Radio?: typeof Radio;
  VARIANTS?: typeof VARIANTS;
} = memo(RadioGroupBase);

RadioGroup.Radio = Radio;
RadioGroup.VARIANTS = VARIANTS;
